//@ts-check

let loadingPromise = null;

export const sleep = (m) => new Promise((r) => setTimeout(r, m));

export async function getfetchopts(opts = {}) {
  const kyopts = {
    // 30 s matches the Lambda timeout, which leaves no headroom for cold start,
    // VPC ENI attach and TLS — long enough for a normal call, but a slow one
    // aborts client-side before the server's own error can be shown. Callers
    // that knowingly wait on a long operation (CSV export) pass a larger value.
    timeout: opts.timeout ?? 30000,
    hooks: {
      beforeRequest: [
        ({ request }) => {
          console.info(`Request: ${request.method} ${request.url}`);
          if (kyopts.json && !opts.noconsole) {
            console.info(JSON.stringify(kyopts.json, null, 2));
          }
        },
      ],
      beforeError: [
        // ky v2 passes hooks a single state object and pre-parses the error
        // response body onto `error.data` (was `await error.response.json()` in v1).
        ({ error }) => {
          if (error.response) {
            if (error.response.status == 401) {
              error.isautherr = true;
            }
            if (error.response.status == 402) {
              error.issuspended = true;
              // Account locked out for non-payment. Divert immediately to the
              // billing settings page so the user sees the recovery flow rather
              // than a stack of error modals from concurrent failing calls.
              if (typeof window !== "undefined" && !window.location.pathname.startsWith("/settings/account/billing/")) {
                window.location.assign("/settings/account/billing/");
              }
            }
            if (error.response.status == 403) {
              error.isauthzerr = true;
            }
            const responsebody = error.data;
            error.body = responsebody;
            if (responsebody?.message) {
              error.message = responsebody.message;
            }
          }
          return error;
        },
      ],
    },
    retry: 0,
  };

  if (opts) {
    Object.assign(kyopts, opts);
  }

  return kyopts;
}

export function uuidv4() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

export function nanoid(t) {
  if (!t) {
    t = 21;
  }
  let e = "";
  let r = crypto.getRandomValues(new Uint8Array(t));
  for (; t--; ) {
    let n = 63 & r[t];
    e += n < 36 ? n.toString(36) : n < 62 ? (n - 26).toString(36).toUpperCase() : n < 63 ? "_" : "-";
  }
  return e;
}

export function rndstr(t = 16, opts = {}) {
  //ascii codes to exclude
  const excludecodes = [58, 59, 60, 61, 62, 63, 64, 91, 92, 93, 94, 95, 96, 79, 83, 66, 73, 90];
  const max = opts.allcaps ? 90 : 122;
  let e = "";
  let r = crypto.getRandomValues(new Uint8Array(32)); //this should give values from 0 - 255
  let idx = 0;
  while (e.length < t) {
    let n = r[idx];
    idx++;
    if (idx >= r.length) {
      r = crypto.getRandomValues(new Uint8Array(32));
      idx = 0;
    }
    if (n < 48 || n > max || excludecodes.includes(n)) {
      continue;
    } else {
      e += String.fromCharCode(n);
    }
  }
  return e;
}

export function rndstrnumeric(t = 10) {
  let e = "";
  let r = crypto.getRandomValues(new Uint8Array(32)); //this should give values from 0 - 255
  let idx = 0;
  while (e.length < t) {
    let n = r[idx];
    idx++;
    if (idx >= r.length) {
      r = crypto.getRandomValues(new Uint8Array(32));
      idx = 0;
    }
    if (n < 48 || n > 57) {
      continue;
    } else {
      e += String.fromCharCode(n);
    }
  }
  return e;
}

export function rndstrnonnumeric(t = 16, opts = {}) {
  //ascii codes to exclude
  const excludecodes = [
    //48 - 57 are numeric. 65 - 90 are uppercase. 97 - 122 are lowercase
    48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 91, 92, 93, 94, 95, 96, 79, 83, 66, 73, 90,
  ];
  const max = opts.allcaps ? 90 : 122;
  let e = "";
  let r = crypto.getRandomValues(new Uint8Array(32)); //this should give values from 0 - 255
  let idx = 0;
  while (e.length < t) {
    let n = r[idx];
    idx++;
    if (idx >= r.length) {
      r = crypto.getRandomValues(new Uint8Array(32));
      idx = 0;
    }
    if (n < 48 || n > max || excludecodes.includes(n)) {
      continue;
    } else {
      e += String.fromCharCode(n);
    }
  }
  return e;
}

export async function goauth() {
  const currentPath = window.location.pathname + window.location.search;
  const isReturnable =
    currentPath.startsWith("/") &&
    !currentPath.startsWith("/login") &&
    !currentPath.startsWith("/auth/");
  const redirect = isReturnable ? currentPath : "/";
  window.location.assign(`/login/?redirect=${encodeURIComponent(redirect)}`);
}

/**
 * True only on local dev (`localhost`/`127.0.0.1`) or a dev/lab host
 * (`*dev.businessmind.app`). Used to relax development-only affordances —
 * currently to bypass the small-screen ("unsupported screen size") gate while
 * the responsive PWA rework is in progress. Prod hosts return false so the gate
 * stays in place there. Temporary: remove once the responsive rework ships and
 * the small-screen gate is retired.
 * @returns {boolean} true on localhost/127.0.0.1 or a dev/lab host, false otherwise
 */
export function isDevOrLabHost() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host.endsWith("dev.businessmind.app");
}

export function datestrformat(datestr, format) {
  try {
    const tz = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dfopts = {
      timeZone: tz,
      literal: "-",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZoneName: "short",
      fractionalSecondDigits: 3,
    };
    const df = new Intl.DateTimeFormat(undefined, dfopts);
    const date = new Date(datestr);
    const dateparts = df.formatToParts(date);
    const dateobj = dateparts.reduce((acc, obj) => {
      if (obj.type === "literal") {
        return acc;
      }
      acc[obj.type] = obj.value;
      return acc;
    }, {});

    if (format == "input-datetime-local") {
      return `${dateobj.year}-${dateobj.month}-${dateobj.day}T${dateobj.hour == "24" ? "00" : dateobj.hour}:${dateobj.minute}:${dateobj.second}`;
    }

    throw new Error(`Unknown date format: ${format}`);
  } catch (error) {
    console.error(error);
  }
}

export function dateformat(date, format) {
  try {
    const tz = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dfopts = {
      timeZone: tz,
      literal: "-",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZoneName: "short",
      fractionalSecondDigits: 3,
    };
    const df = new Intl.DateTimeFormat(undefined, dfopts);
    const dateparts = df.formatToParts(date);
    const dateobj = dateparts.reduce((acc, obj) => {
      if (obj.type === "literal") {
        return acc;
      }
      acc[obj.type] = obj.value;
      return acc;
    }, {});

    if (format == "yyyyMMddHHmmss") {
      return `${dateobj.year}${dateobj.month}${dateobj.day}${dateobj.hour == "24" ? "00" : dateobj.hour}${dateobj.minute}${dateobj.second}`;
    }

    if (format == "yyyy-MM-dd") {
      return `${dateobj.year}-${dateobj.month}-${dateobj.day}`;
    }

    if (format == "input-datetime-local") {
      return `${dateobj.year}-${dateobj.month}-${dateobj.day}T${dateobj.hour == "24" ? "00" : dateobj.hour}:${dateobj.minute}:${dateobj.second}`;
    }

    throw new Error(`Unknown date format: ${format}`);
  } catch (error) {
    console.error(error);
  }
}

export function getMonthName(locale, monthNumber, format = "long") {
  const date = new Date();
  date.setMonth(monthNumber - 1); // Subtract 1 since months are zero-indexed
  return date.toLocaleString(locale, { month: format });
}

export function validateEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function validatePhone(phone) {
  if (!phone || typeof phone !== "string") {
    return false;
  }

  // Remove all non-digit characters except the leading +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // Check if it contains any letters (original validation)
  if (/[a-zA-Z]/.test(phone)) {
    return false;
  }

  // Handle different formats
  if (cleaned.startsWith("+1") && cleaned.length === 12) {
    // Already in +1XXXXXXXXXX format
    return true;
  } else if (cleaned.startsWith("1") && cleaned.length === 11) {
    // 1XXXXXXXXXX format
    return true;
  } else if (cleaned.length === 10) {
    // XXXXXXXXXX format
    return true;
  }

  return false;
}

export function formatSMSNumber(phone) {
  // Remove all non-digit characters except the leading +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // Handle different formats and convert to +1XXXXXXXXXX
  if (cleaned.startsWith("+1")) {
    // Already in +1XXXXXXXXXX format
    return cleaned;
  } else if (cleaned.startsWith("1")) {
    // 1XXXXXXXXXX format - add the +
    return "+" + cleaned;
  } else {
    // XXXXXXXXXX format - add +1
    return "+1" + cleaned;
  }
}

export async function loadBrowserPrint() {
  if (window.BrowserPrint) {
    return window.BrowserPrint;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "/BrowserPrint-3.1.250.min.js";
    script.onload = () => {
      resolve(window.BrowserPrint);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return loadingPromise;
}

/**
 * Route to the record a document line was picked from, or null for a free-typed line.
 * product_id is checked first: SKU lines also carry their model_id, and the SKU is the item sold.
 */
export function lineItemHref(line) {
  if (line.product_id) {
    return `/sku/?id=${line.product_id}`;
  }
  if (line.job_id) {
    return `/job/?id=${line.job_id}`;
  }
  if (line.giftcard_id) {
    return `/giftcard/?id=${line.giftcard_id}`;
  }
  if (line.model_id) {
    return `/model/?id=${line.model_id}`;
  }
  return null;
}
